import { DwQueryRequest, Field } from '@fabric-ui/components';
import { FileFormat } from '@fabric-ui/components/Table';
import {
  AssetCloud,
  AssetDataBase,
  AssetDefault,
  AssetFiles,
  AssetHost,
  AssetImage,
  AssetMobileDevice,
  AssetNetDevice,
  AssetRepo,
  AssetUser,
} from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { abbreviateNumber, capitalizeSentence } from '@fabric-ui/utils';
import { capitalize } from '@mui/material';
import { DimensionTypes } from '@views/CustomDashboards/constants';
import {
  AllTypeWidgets,
  PivotTableTypeWidget,
  ReportsTypes,
  SavedAllTypeWidgets,
  SavedPivotTable,
  TableSubType,
} from '@views/CustomDashboards/types';
import { Measurement } from '@views/Measurements/types';
import cronstrue from 'cronstrue';
import { isDate } from 'date-fns';
import DOMPurify from 'dompurify';

export const isMetric = (possibleMetricName: string, metrics: Measurement[]) =>
  !!metrics.find(({ systemName }) => systemName === possibleMetricName);

export const commonFormStyles = {
  horizontalForm: {
    ...flex.itemsCenter,
    gap: '16px',
    '& .switchToggle': {
      marginLeft: '8px',
      // alignSelf: ({ error }) => (error ? '' : 'end'),
      height: '40px',
      alignSelf: 'end',
      pt: '24px',
    },
  },
};

export const ConditionalWrapper = ({ condition, wrapper, children }) => (condition ? wrapper(children) : children);

export const generateID = () => Math.random().toString(36).slice(-6);

export const generateUUID = () => crypto.randomUUID();

export const userRoleMap = {
  RND_ADMIN: 'Avalor Admin',
  ADMIN: 'Administrator',
  EDITOR: 'Editor',
  VIEWER: 'Viewer',
  NO_ACCESS: 'No Access',
  SIEM: 'SIEM',
  PRE_POV: 'Pre POV',
  SYS_ADMIN: 'Sys Admin',
};

export const visuallyHidden = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
};

export const camelCaseToTitleCase = str => {
  const result = str.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
};
export const formatStartEachWordWithCapitalLetter = sentence =>
  sentence ? capitalizeSentence(sentence.replace(/_/g, ' ').toLowerCase()) : sentence;

export const mapValueCapitalizeFunc = v =>
  v
    .split('_')
    .map(s => `${capitalize(s.toLowerCase())}`)
    .join(' ');

export const formDisabledStyle = {
  filter: 'opacity(0.5)',
  pointerEvents: 'none',
};

export const isValidHttpUrl = string => {
  try {
    const url = new URL(string);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

export function arrayToCsv(data) {
  return data
    .map(row =>
      row
        .map(v => `"${String(v).replaceAll('"', '""')}"`) // quote it
        .join(',')
    )
    .join('\r\n'); // comma-separated rows starting on new lines
}

export function downloadBlob(content, contentType, fileName) {
  // Create a blob
  const blob = new Blob([content], { type: contentType });
  const url = URL.createObjectURL(blob);

  // Create a link to download it
  const pom = document.createElement('a');
  pom.href = url;
  pom.setAttribute('download', fileName);
  pom.click();
}

export const downloadURI = (uri, name?, target?: string) => {
  if (target || name) {
    const link = document.createElement('a');
    link.download = name;
    link.href = uri;
    if (target) {
      link.target = target;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = uri;
    document.body.appendChild(iframe);

    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 10000);
  }
};

export const truncateDecimal = (num, digits) => Math.trunc(num * 10 * digits) / (10 * digits);
export const getPercent = num => `${Math.round(num * 100) / 100}%`;

/**
 * @deprecated - use typed setNestedValue instead
 */
export const setNestedValue = (path, obj, value) => {
  const setValue = (string, obj, value) => {
    const [current, ...rest] = string.split('.');
    if (rest.length >= 1) {
      // eslint-disable-next-line no-param-reassign
      setValue(rest.join('.'), (obj[current] = obj[current] || {}), value);
    } else {
      // eslint-disable-next-line no-param-reassign
      obj[current] = value;
    }
    return obj;
  };
  return setValue(path, structuredClone(obj), value);
};
export const getNestedValue = (string, obj) => {
  const [current, ...rest] = string.split('.');
  if (rest.length > 0) {
    return getNestedValue(rest.join('.'), obj[current] || {});
  }
  return obj[current];
};

export const generateOption = value => ({ title: value, value });

export const getDaysUntilOrSinceExpiration = (expiresAt?: string | null, mode: 'until' | 'since' = 'until'): number | null => {
  if (!expiresAt) return null;
  const now = new Date();
  const expiration = new Date(expiresAt);

  const diffMs = mode === 'until' ? expiration.getTime() - now.getTime() : now.getTime() - expiration.getTime();
  const round = mode === 'until' ? Math.ceil : Math.floor;

  return Math.max(0, round(diffMs / (1000 * 60 * 60 * 24)));
};

export const getMatchStringValuesInExpression = expression => {
  if (expression.includes('"')) {
    const matches = expression.match(/"([^"]*)"/g);
    return matches ? matches.map(m => m.slice(1, -1)) : [];
  }
  return [expression];
};
const mapValueFunc = v => (typeof v === 'string' ? `'${v.replaceAll("'", "''")}'` : 'null');
export const generateStringList = (arr: string[] = []) => arr.map(mapValueFunc).join(', ');

export const flattenKeys = obj => {
  const result: string[] = [];

  function recurse(obj, currentPath) {
    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const key in obj) {
      const newPath = currentPath ? `${currentPath}.${key}` : key;
      if (typeof obj[key] === 'object' && !isDate(obj[key])) {
        recurse(obj[key], newPath);
      } else {
        result.push(newPath);
      }
    }
  }

  recurse(obj, '');
  return result;
};

export const prettyFieldName = field =>
  field
    .split(/[._]/)
    .map(w => (['ip', 'os', 'cve', 'cwe', 'fqdn', 'id', 'url', 'sla'].includes(w) ? w.toUpperCase() : capitalize(w)))
    .join(' ');

export const sanitize = dirty => DOMPurify.sanitize(dirty);

export function removeNodeFromObject(obj, node) {
  // Check if the object is null or not an object
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    // Create a new array without the node
    return obj.filter(item => item !== node).map(item => removeNodeFromObject(item, node));
  }
  // Create a new object without the node
  const newObj = {};

  Object.entries(obj).forEach(([key, value]) => {
    if (value !== node) {
      newObj[key] = removeNodeFromObject(value, node);
    }
  });

  return newObj;
}

export const orderByKey = (array, key, isAsc = true) =>
  [...array].sort((a, b) => {
    const valA = (typeof a[key] === 'string' ? a[key].toLowerCase() : a[key]) ?? '';
    const valB = (typeof b[key] === 'string' ? b[key].toLowerCase() : b[key]) ?? '';
    if (valA < valB) {
      return isAsc ? -1 : 1;
    }
    if (valA > valB) {
      return isAsc ? 1 : -1;
    }
    return 0;
  });

export function moveElementToEnd(array, element) {
  const index = array.indexOf(element);

  if (index !== -1) {
    return [...array.filter((el, i) => i !== index), element];
  }

  return array;
}

export const getFieldDisplayName = (displayName, group) => `${group} ${displayName}`;

export const isNullOrUndefined = (value: unknown): value is null | undefined => value === null || value === undefined;

export const isArrayofArrays = arr => Array.isArray(arr) && arr.every(element => Array.isArray(element)) && arr.length;

const getFilteredList = (list, item, filterKey) =>
  list.filter(v => (filterKey === undefined ? v !== item : v[filterKey] !== item[filterKey]));

export const rearrange = (list, item, newIndex, filterKey, hasMoved = false, isTableV2 = false) => {
  const filteredList = getFilteredList(list, item, filterKey);
  const newItem =
    filterKey === undefined
      ? item
      : isTableV2
        ? { ...item, meta: { ...item?.meta, hidden: hasMoved ? !item.meta?.hidden : item.meta?.hidden } }
        : { ...item, hidden: hasMoved ? !item.hidden : item.hidden };

  if (newIndex === list.length - (hasMoved ? 0 : 1)) {
    return [...filteredList, newItem];
  }
  return (hasMoved ? list : filteredList).reduce(
    (arr, el, index) =>
      [...arr, index === newIndex && newItem, (filterKey === undefined ? el !== item : el[filterKey] !== item[filterKey]) && el].filter(
        d => d
      ),
    []
  );
};

export const rearrangeObject = (obj, order) => {
  const orderSet = new Set(order);
  const reordered = order.reduce((acc, key) => {
    if (key in obj) {
      return { ...acc, [key]: obj[key] };
    }
    return acc;
  }, {});
  const rest = Object.fromEntries(Object.entries(obj).filter(([key]) => !orderSet.has(key)));
  return { ...reordered, ...rest, forceRender: [Date.now()] };
};

export const isJSON = value => typeof value === 'object' && !Array.isArray(value) && value !== null;

export const isJsonAndNotEmpty = value => isJSON(value) && Object.keys(value).length !== 0;

export const getEscapedValue = value => (['boolean', 'number'].includes(typeof value) ? value : value?.replaceAll("'", "''") || null);

export const getEscapedValueForSQLLike = value => (typeof value === 'string' ? value.replace(/[_%]/g, '\\$&') : value);

export const numToFixed = num => {
  const parsedNum = parseFloat(num);
  return Number.isNaN(parsedNum) ? num : parsedNum % 1 === 0 ? parsedNum.toLocaleString() : abbreviateNumber(num);
};

export function mergeObjects(obj1, obj2) {
  return Object.keys(obj2).reduce(
    (merged, key) => {
      if (typeof obj2[key] === 'object' && obj2[key] !== null && !Array.isArray(obj2[key])) {
        // If the value in obj2 is an object and not null or an array, recursively merge it
        return { ...merged, [key]: mergeObjects(merged[key] || {}, obj2[key]) };
      }
      // Otherwise, assign the value from obj2 to the merged object
      return { ...merged, [key]: obj2[key] };
    },
    { ...obj1 }
  );
}

export function extractKeyValues(obj, keyToExtract, resultArray: any[] = []) {
  if (typeof obj !== 'object' || obj === null) {
    return resultArray;
  }

  if (obj[keyToExtract] !== undefined) {
    resultArray.push(obj[keyToExtract]);
  }

  Object.values(obj).forEach(value => {
    if (typeof value === 'object') {
      extractKeyValues(value, keyToExtract, resultArray);
    }
  });

  return resultArray;
}

export function recursiveKeyUpdate(obj, targetKey, targetValuePredicate, getNewValue) {
  if (Array.isArray(obj)) {
    return obj.map(item => recursiveKeyUpdate(item, targetKey, targetValuePredicate, getNewValue));
  }

  if (typeof obj !== 'object' || obj[targetKey] === null) {
    return obj;
  }

  const updatedObj = {};
  if (obj) {
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      if (key === targetKey && targetValuePredicate(value)) {
        updatedObj[key] = getNewValue(value);
      } else {
        updatedObj[key] = recursiveKeyUpdate(value, targetKey, targetValuePredicate, getNewValue);
      }
    });
  }

  return updatedObj;
}

export function getFieldName(str = '') {
  const dotIndex = str.lastIndexOf('.');
  return dotIndex === -1 ? str : str.slice(dotIndex + 1);
}

export const sqlFormatter = (sqlString = '') => sqlString.replaceAll('\n', ' ');

export const sortByStringField = (arr, field) => {
  arr.sort((a, b) => {
    const stringA = a[field].toLowerCase();
    const stringB = b[field].toLowerCase();

    return stringA.localeCompare(stringB); // Use localeCompare for case-insensitive sorting
  });

  return arr;
};

const assetStyle = { width: 24, aspectRatio: 1, alignSelf: 'center' };
const assetCloud = <AssetCloud style={assetStyle} />;
const assetHost = <AssetHost style={assetStyle} />;
const assetDefault = <AssetDefault style={assetStyle} />;
const assetDatabase = <AssetDataBase style={assetStyle} />;
const assetImage = <AssetImage style={assetStyle} />;
const assetMobileDevice = <AssetMobileDevice style={assetStyle} />;
const assetUser = <AssetUser style={assetStyle} />;
const assetRepo = <AssetRepo style={assetStyle} />;
const assetFiles = <AssetFiles style={assetStyle} />;
const assetNetDevice = <AssetNetDevice style={assetStyle} />;
const ASSET_TYPE_ICON_MAP = {
  'Windows Server': assetCloud,
  'Linux Server': assetCloud,
  Workstation: assetHost,
  'Mobile Device': assetMobileDevice,
  Unknown: assetDefault,
  Default: assetDefault,
  Storage: assetDatabase,
  Host: assetHost,
  Containers: assetDefault,
  Image: assetImage,
  Profile: assetUser,
  VPN: assetCloud,
  'Web Application': assetCloud,
  Container: assetCloud,
  Hypervisor: assetHost,
  Linux: assetHost,
  Unix: assetHost,
  'Windows Workstation': assetHost,
  'Mac Workstation': assetHost,
  'Unix Server': assetHost,
  'Virtual Machine': assetHost,
  Nutanix: assetHost,
  Mainframe: assetHost,
  AIX: assetHost,
  Database: assetDatabase,
  Repository: assetRepo,
  SAN: assetFiles,
  NAS: assetFiles,
  Firewall: assetNetDevice,
  'IoT Device': assetNetDevice,
  'Load Balancer': assetNetDevice,
  'Networking Device': assetNetDevice,
  Printer: assetNetDevice,
};

export const getAssetTypeIcon = assetType => ASSET_TYPE_ICON_MAP[assetType] || ASSET_TYPE_ICON_MAP.Default;

export const serializeSortItem = ({ property, isAsc }) => `${property}__${isAsc ? 'ASC' : 'DESC'}`;
export const deserializeSortItem = item => ({ property: item.split('__')[0], isAsc: item.split('__')[1] === 'ASC' });
export const isFieldEmpty = field => field === null || field === undefined || field === '';

export const cronExpressionToString = expression => {
  try {
    return cronstrue.toString(expression);
  } catch {
    return '';
  }
};

export const formatCronValue = val => val?.slice(2).replaceAll('?', '*');

export const isValidJsonString = val => {
  try {
    return isJSON(JSON.parse(val));
  } catch {
    return false;
  }
};

export const is100PercentValue = (key, series) => series.some(({ dataKey, is100Percent }) => dataKey === key && is100Percent);

export const normalize100PercentData = (data, series) =>
  data.map(entry => {
    const total = Object.entries(entry).reduce(
      (acc, [key, value]) => acc + (typeof value === 'number' && is100PercentValue(key, series) ? value : 0),
      0
    );
    return Object.entries(entry).reduce((acc, [key, value]) => {
      acc[key] =
        typeof value === 'number' && is100PercentValue(key, series) && total
          ? (Math.floor((value / total) * 10000) / 100).toFixed(2)
          : value;
      return acc;
    }, {});
  });

export const EMAIL_REGEX_PATTERN =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export const isValidEmail = email => String(email).toLowerCase().match(EMAIL_REGEX_PATTERN);
export const getNetworkErrorCode = error =>
  error?.status ||
  error?.response?.errors?.[0]?.extensions?.status ||
  error?.response?.status ||
  error?.extensions?.status ||
  error?.[0]?.extensions?.status;

export const alphaSolid = (rgbHex, a, bgHex = '#FFFFFF') => {
  // Parse the RGB components
  const r = parseInt(rgbHex.slice(1, 3), 16);
  const g = parseInt(rgbHex.slice(3, 5), 16);
  const b = parseInt(rgbHex.slice(5, 7), 16);

  // Parse the background RGB components
  const br = parseInt(bgHex.slice(1, 3), 16);
  const bg = parseInt(bgHex.slice(3, 5), 16);
  const bb = parseInt(bgHex.slice(5, 7), 16);

  // Calculate the resulting RGB components
  const rr = Math.round((1 - a) * br + a * r);
  const rg = Math.round((1 - a) * bg + a * g);
  const rb = Math.round((1 - a) * bb + a * b);

  // Convert the resulting RGB components to hex
  // eslint-disable-next-line no-bitwise
  return `#${((1 << 24) + (rr << 16) + (rg << 8) + rb).toString(16).slice(1).toUpperCase()}`;
};

export const convertMinutesToMilliSeconds = minutes => minutes * 60000;
export const convertFromSecondsToMilliseconds = seconds => seconds * 1000;

export const sum = (...numbers: number[]) => numbers.reduce((curr, acc) => curr + acc, 0);

export const getSelectedFieldsData = (dims: Field[], metrics: Field[]) => ({
  ...dims.reduce((acc, dim) => ({ ...acc, [dim.name]: dim }), {}),
  ...metrics.reduce((acc, metric) => ({ ...acc, [metric.name]: metric }), {}),
});

export const parseDims = (dims: Field[]) =>
  dims.map(({ name, type, expression, alias, ...rest }) =>
    expression && alias ? { ...rest, alias, expression } : { ...rest, name, alias }
  );

export const getDimsWithAlias = (dims: Field[], key: 'alias' | 'name' = 'alias') =>
  dims.reduce((acc, dim) => {
    if (dim.alias && dim.expression) {
      return {
        ...acc,
        [`${dim[key]}`]: dim,
      };
    }
    return acc;
  }, {});

export const getFileFormat = (type: ReportsTypes) => (type === ReportsTypes.Visual ? FileFormat.PDF_FORMAT : FileFormat.CSV_FORMAT);

export const parseWidgetDataBeforeApiRequest = ({
  widget,
  isSavingWidget = false,
  metricsWithAliases,
}: {
  widget: AllTypeWidgets;
  isSavingWidget?: boolean;
  metricsWithAliases: { systemName: string; alias?: string }[];
}): Exclude<SavedAllTypeWidgets, PivotTableTypeWidget> | SavedPivotTable => {
  if (widget.type === TableSubType.PivotTable) {
    if (widget.requests[0].select.dims.find(({ type }) => type === DimensionTypes.Col)) {
      return {
        ...widget,
        definition: { ...widget.definition, custom: { ...widget.definition.custom, sortingCol: widget.requests[0].sortingCol } },
        requests: widget.requests.map(request => {
          const { sortingCol, metricsFilterThreshold, sorting, groupBy, ...rest } = request;
          return {
            ...rest,
            groupBy: groupBy?.filter(group => !request.select.dims.find(({ alias }) => group === alias)?.expression),
            timezoneOffset: new Date().getTimezoneOffset(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            ...(!(isSavingWidget && sorting[0]?.path && sorting[0].path.path.find(({ name }) => name === 'buckets'))
              ? { sorting }
              : { sorting: [] }),
            select: {
              dims: parseDims(rest.select.dims.filter(({ type }) => type === DimensionTypes.Row)),
              metrics: [
                {
                  name: 'pivot',
                  alias: 'pivot',
                  conf: {
                    pivot: {
                      metrics: rest.select.metrics,
                      pivotDimension: parseDims(rest.select.dims.filter(({ type }) => type === DimensionTypes.Col))[0] ?? {},
                      enableRowTotals: widget.definition.custom.showColTotals,
                      pivotFilter: {
                        pivotDimApproxTopK: {
                          k: 10,
                        },
                      },
                    },
                  },
                },
              ],
            },
          };
        }),
      };
    }
    return {
      ...widget,
      requests: widget.requests.map(request => {
        const { sortingCol, ...rest } = request;

        return {
          ...rest,
          timezoneOffset: new Date().getTimezoneOffset(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          select: {
            ...request.select,
            dims: parseDims(request.select.dims),
            metrics: request.select.metrics.map(m => ({
              ...m,
              name: metricsWithAliases.find(metric => metric.alias === m.alias)?.systemName || m.name,
            })),
          },
        };
      }),
    };
  }

  if (widget.type === TableSubType.Table) {
    const size = widget.definition.custom.topN || widget.requests.at(0)?.top?.size || widget.definition.custom.pageSize || 25;
    return {
      ...widget,
      requests: widget.requests.map((request: DwQueryRequest) => ({
        ...request,
        timezoneOffset: new Date().getTimezoneOffset(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        select: {
          ...request.select,
          dims: parseDims(request.select.dims),
          metrics: request.select.metrics.map(m => ({
            ...m,
            name: metricsWithAliases.find(metric => metric.alias === m.alias)?.systemName || m.name,
          })),
        },
        top: {
          ...request.top,
          groupOthers: !!request.top?.groupOthers,
          size,
        },
      })),
    };
  }

  return {
    ...widget,
    requests: widget.requests.map((request: DwQueryRequest) => ({
      ...request,
      timezoneOffset: new Date().getTimezoneOffset(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      select: {
        ...request.select,
        dims: parseDims(request.select.dims),
        metrics: request.select.metrics.map(m => ({
          ...m,
          name: metricsWithAliases.find(metric => metric.alias === m.alias)?.systemName || m.name,
        })),
      },
    })),
  };
};

/**
 * Extracts the Field name from a dimension's expression object.
 * The Field name is expected to be in expression.function.args[0].symbol.ref
 * @param expression The expression object from a dimension.
 * @returns The Field name string
 */
export const getFieldNameFromExpression = (expression: any): string => expression.function.args[0].symbol.ref;

/**
 * Parses an array of dimension objects. If a dimension has a null name but has an expression and an alias,
 * it attempts to extract the Field name from the expression and assign it to the name property.
 *
 * @param dims - The array of dimension objects to parse.
 * @returns A new array of parsed dimension objects.
 */
export const parseDimsWithExpression = (dims: (Omit<Field, 'name'> & { name: null | string })[]): Field[] => {
  if (!Array.isArray(dims)) {
    return [];
  }

  return dims.map(dim =>
    dim.name === null && dim.expression && dim.alias ? { ...dim, name: getFieldNameFromExpression(dim.expression) } : (dim as Field)
  );
};

export const isBoolean = (variable: unknown): variable is boolean => typeof variable === 'boolean';

export const mapAliasToName = (data: Record<string, any>[], dimsWithAlias: Record<string, any>) =>
  data.map(item => {
    const parsedItem = Object.keys(item).reduce((acc, key) => ({ ...acc, [dimsWithAlias[key]?.name || key]: item[key] }), {});
    return parsedItem;
  });

/**
 * Resolves a CSS custom property (variable) to its computed value.
 * If the color is not a CSS variable or cannot be resolved, returns the original color.
 * @param color - The color string, potentially a CSS variable like 'var(--color-name)'
 * @returns The resolved color value or the original color if not a CSS variable
 */
export const resolveCssVariable = (color: string): string => {
  if (typeof window !== 'undefined' && color?.startsWith('var(--')) {
    const varName = color.match(/var\((--[^,)]+)/)?.[1];
    if (varName) {
      const computed = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
      if (computed) return computed;
    }
  }
  return color;
};
